import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { API } from "@/context/AuthContext";

const BLOCKS = [["consignor", "Consignor block"], ["bank", "Bank details"], ["correspondence", "Correspondence address"], ["signatory", "Signatory (2 lines)"], ["footer", "Footer"]];
const TEXTS = [["company_title", "Company title"], ["doc_title", "Document title"], ["contact", "Default contact"], ["phone", "Default phone"], ["email", "Default email"]];

export function PISettingsDialog({ open, onClose, onSaved }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) axios.get(`${API}/proforma/settings`).then((r) => setS(r.data)).catch(() => toast.error("Could not load settings")); }, [open]);
  const save = async () => {
    setSaving(true);
    try {
      const body = { ...s, next_numbers: { [s.fy]: Number(s.next_numbers?.[s.fy] || 1) } };
      const r = await axios.put(`${API}/proforma/settings`, body);
      toast.success(`Saved — next PI will be ${r.data.next_number}`); onSaved?.(r.data); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0 gap-0" data-testid="pi-settings-dialog">
        <DialogHeader className="px-6 pt-6 pb-3"><DialogTitle>Proforma Invoice settings</DialogTitle>
          <DialogDescription>Number series and the fixed text printed on every PI.</DialogDescription></DialogHeader>
        {s && (
          <div className="overflow-y-auto px-6 pb-4 space-y-4 flex-1 min-h-0">
            <div className="grid grid-cols-3 gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div><Label className="text-xs">Prefix</Label><Input value={s.prefix} onChange={(e) => setS({ ...s, prefix: e.target.value })} data-testid="pi-set-prefix" /></div>
              <div><Label className="text-xs">Next number ({s.fy})</Label>
                <Input type="number" min="1" value={s.next_numbers?.[s.fy] || 1} onChange={(e) => setS({ ...s, next_numbers: { ...(s.next_numbers || {}), [s.fy]: e.target.value } })} data-testid="pi-set-next" /></div>
              <div><Label className="text-xs">Retention % (default)</Label><Input type="number" step="any" value={s.retention_pct} onChange={(e) => setS({ ...s, retention_pct: Number(e.target.value) })} data-testid="pi-set-retention" /></div>
              <p className="col-span-3 text-xs text-slate-500">Format: <b>{s.prefix ? `${s.prefix}/` : ""}{String(s.next_numbers?.[s.fy] || 1).padStart(2, "0")}/{s.fy}</b> — the number goes up by one each time a PI is generated, and restarts at 1 every financial year.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {TEXTS.map(([k, l]) => <div key={k}><Label className="text-xs">{l}</Label><Input value={s[k] || ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} data-testid={`pi-set-${k}`} /></div>)}
            </div>
            {BLOCKS.map(([k, l]) => (
              <div key={k}><Label className="text-xs">{l} — one line per row</Label>
                <Textarea rows={Math.max(2, (s[k] || []).length)} value={(s[k] || []).join("\n")} onChange={(e) => setS({ ...s, [k]: e.target.value.split("\n") })} data-testid={`pi-set-${k}`} /></div>
            ))}
          </div>
        )}
        <DialogFooter className="px-6 py-4 border-t border-slate-100">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={!s || saving} data-testid="pi-settings-save">{saving ? "Saving…" : "Save settings"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
